// CriteriaFormCard.jsx — Carte d'interaction « affiner ta recherche » (owner)
// ─────────────────────────────────────────────────────────────────────────────
// Pour les recherches complexes (immobilier, véhicule), AYA demande les détails
// dont elle a besoin via une belle carte DÉTERMINISTE (registre déclaratif —
// le LLM ne construit jamais l'interface). À la soumission, la requête enrichie
// est envoyée comme un message naturel → pipeline normal.
// Palette : émeraude / ivoire / gold champagne. Thème clair/sombre auto.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";

const GOLD_GRAD = "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)";

const COMMUNES = ["Cocody", "Yopougon", "Plateau", "Treichville", "Marcory", "Abobo", "Adjamé", "Koumassi", "Port-Bouët", "Bingerville", "Autre ville"];

// Registre DÉCLARATIF des domaines — ajouter un domaine = une entrée, zéro code.
const DOMAINS = {
  immobilier: {
    icon: "🏠",
    title: "Précisons ta recherche immobilière",
    fields: [
      { id: "type", label: "Type de bien", kind: "chips", options: ["Appartement", "Studio", "Villa", "Maison", "Terrain"], default: "Appartement" },
      { id: "deal", label: "Location ou achat ?", kind: "chips", options: ["Location", "Achat"], default: "Location" },
      { id: "commune", label: "Ville / commune", kind: "select", options: COMMUNES },
      { id: "chambres", label: "Chambres", kind: "chips", options: ["1", "2", "3", "4+"], optional: true },
      { id: "budget", label: "Budget max (FCFA)", kind: "number", placeholder: "Ex : 300 000" },
    ],
    compose: (v) =>
      `Je cherche ${v.type === "Appartement" ? "un appartement" : v.type === "Studio" ? "un studio" : v.type === "Terrain" ? "un terrain" : `une ${v.type.toLowerCase()}`}` +
      `${v.chambres ? ` ${v.chambres} chambre${v.chambres === "1" ? "" : "s"}` : ""}` +
      `${v.commune ? ` à ${v.commune}` : ""} en ${v.deal.toLowerCase()}` +
      `${v.budget ? `, budget max ${Number(v.budget).toLocaleString("fr-FR")} FCFA${v.deal === "Location" ? "/mois" : ""}` : ""}.`,
  },
  vehicule: {
    icon: "🚗",
    title: "Précisons ta recherche de véhicule",
    fields: [
      { id: "type", label: "Type", kind: "chips", options: ["Voiture", "Moto", "Utilitaire"], default: "Voiture" },
      { id: "marque", label: "Marque", kind: "select", options: ["Toyota", "Hyundai", "Kia", "Suzuki", "Renault", "Peugeot", "Mercedes", "BMW", "Autre / peu importe"], optional: true },
      { id: "etat", label: "État", kind: "chips", options: ["Occasion", "Neuf", "Peu importe"], default: "Occasion" },
      { id: "commune", label: "Ville / commune", kind: "select", options: COMMUNES, optional: true },
      { id: "budget", label: "Budget max (FCFA)", kind: "number", placeholder: "Ex : 5 000 000" },
    ],
    compose: (v) =>
      `Je cherche ${v.type === "Voiture" ? "une voiture" : v.type === "Moto" ? "une moto" : "un utilitaire"}` +
      `${v.marque && !v.marque.startsWith("Autre") ? ` ${v.marque}` : ""}` +
      `${v.etat && v.etat !== "Peu importe" ? ` ${v.etat.toLowerCase()}` : ""}` +
      `${v.commune ? ` à ${v.commune}` : ""}` +
      `${v.budget ? `, budget max ${Number(v.budget).toLocaleString("fr-FR")} FCFA` : ""}.`,
  },
};

export default function CriteriaFormCard({ domain, onSubmit }) {
  const spec = DOMAINS[domain];
  const [values, setValues] = useState(() => {
    const init = {};
    (spec?.fields || []).forEach((f) => { if (f.default) init[f.id] = f.default; });
    return init;
  });
  const [sent, setSent] = useState(false);
  if (!spec) return null;

  const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));
  const canSubmit = !sent && spec.fields.filter((f) => !f.optional && f.kind !== "number").every((f) => values[f.id]);

  const submit = () => {
    if (!canSubmit) return;
    setSent(true);
    onSubmit && onSubmit(spec.compose(values));
  };

  return (
    <div
      data-testid="criteria-form-card"
      style={{
        maxWidth: 520,
        marginTop: 10,
        background: "linear-gradient(150deg, #0B4531 0%, #062A1F 100%)",
        border: "1px solid rgba(232, 213, 163, 0.4)",
        borderRadius: 16,
        overflow: "hidden",
        color: "#F4F1E8",
        boxShadow: "0 8px 26px rgba(6, 42, 31, 0.3)",
        opacity: sent ? 0.65 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", background: "rgba(232,213,163,0.1)", borderBottom: "1px solid rgba(232,213,163,0.25)" }}>
        <span style={{ fontSize: 17 }} aria-hidden="true">{spec.icon}</span>
        <span style={{ fontSize: 13.5, fontWeight: 800 }}>{spec.title}</span>
      </div>

      <div style={{ padding: "12px 14px 14px" }}>
        {spec.fields.map((f) => (
          <div key={f.id} style={{ marginBottom: 11 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#EBD9A8", marginBottom: 5 }}>
              {f.label}{f.optional ? <span style={{ color: "rgba(244,241,232,0.4)", fontWeight: 500 }}> (optionnel)</span> : ""}
            </div>
            {f.kind === "chips" && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {f.options.map((o) => {
                  const on = values[f.id] === o;
                  return (
                    <button key={o} type="button" disabled={sent}
                      data-testid={`criteria-${f.id}-${o}`}
                      onClick={() => set(f.id, on && f.optional ? "" : o)}
                      style={{
                        padding: "7px 13px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
                        border: `1px solid ${on ? "rgba(232,213,163,0.8)" : "rgba(244,241,232,0.25)"}`,
                        background: on ? GOLD_GRAD : "transparent",
                        color: on ? "#3A2E10" : "rgba(244,241,232,0.75)",
                      }}>
                      {o}
                    </button>
                  );
                })}
              </div>
            )}
            {f.kind === "select" && (
              <select
                value={values[f.id] || ""}
                disabled={sent}
                onChange={(e) => set(f.id, e.target.value)}
                data-testid={`criteria-${f.id}`}
                style={{ width: "100%", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(232,213,163,0.3)", background: "rgba(4,24,15,0.6)", color: "#F4F1E8", fontSize: 13 }}
              >
                <option value="">— Choisir —</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {f.kind === "number" && (
              <input
                type="number"
                inputMode="numeric"
                value={values[f.id] || ""}
                disabled={sent}
                onChange={(e) => set(f.id, e.target.value)}
                placeholder={f.placeholder}
                data-testid={`criteria-${f.id}`}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(232,213,163,0.3)", background: "rgba(4,24,15,0.6)", color: "#F4F1E8", fontSize: 13 }}
              />
            )}
          </div>
        ))}

        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          data-testid="criteria-submit"
          style={{
            width: "100%", padding: "11px 14px", borderRadius: 11, border: "none",
            background: canSubmit ? GOLD_GRAD : "rgba(244,241,232,0.15)",
            color: canSubmit ? "#3A2E10" : "rgba(244,241,232,0.4)",
            fontWeight: 800, fontSize: 13.5, cursor: canSubmit ? "pointer" : "default",
            boxShadow: canSubmit ? "0 4px 14px rgba(205,164,82,0.35)" : "none",
          }}
        >
          {sent ? "Recherche lancée ✓" : "🔎 Lancer la recherche"}
        </button>
      </div>
    </div>
  );
}
